<template>
  <div>
    <v-dialog v-model="dialog" persistent scrollable max-width="760px">
      <v-card v-if="Student" class="profile-dialog-card entity-form-dialog">
        <EntityDialogHeader eyebrow="Student enrollment" :title="Student.fullName" subtitle="Manage departments" icon="mdi-school-outline" close-label="Close departments" @close="close" />
        <v-divider />
        <v-card-text class="profile-dialog-body entity-form-dialog__body">
          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading"><span><v-icon size="19" color="primary">mdi-school-outline</v-icon></span><div><h3>Current departments ({{ assignments.length }})</h3><p>Edit tuition or remove an existing assignment.</p></div></div>
            <div v-if="loadingAssignments" class="text-center py-4"><v-progress-circular indeterminate color="primary" /></div>
            <div v-else-if="assignments.length">
              <div v-for="item in assignments" :key="item.id" class="student-management-row">
                <div><strong>{{ item.Class ? item.Class.name : "Department" }}</strong><small>{{ Number(item.parentPay || 0) | currency }} parent responsibility</small></div>
                <v-btn icon small aria-label="Edit department tuition" @click="editFee(item)"><v-icon size="18">mdi-pencil-outline</v-icon></v-btn>
                <v-btn icon small color="error" aria-label="Remove department" @click="remove(item)"><v-icon size="18">mdi-delete-outline</v-icon></v-btn>
              </div>
            </div>
            <p v-else class="mb-0">No departments assigned.</p>
          </section>
          <section v-if="!Student.leftDate" class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading"><span><v-icon size="19" color="primary">mdi-plus</v-icon></span><div><h3>Add a department</h3><p>A student may have more than one department.</p></div></div>
            <v-autocomplete v-model="selectedClass" :items="availableClasses" item-text="name" return-object outlined dense clearable hide-details label="Department" :loading="loadingClasses" />
            <v-row v-if="selectedClass" class="mt-2">
              <v-col cols="12" sm="6"><v-text-field v-model.number="fee" type="number" min="0" outlined dense hide-details label="Monthly fee" /></v-col>
              <v-col cols="12" sm="6"><v-text-field v-model.number="discount" type="number" min="0" outlined dense hide-details label="Discount" /></v-col>
              <v-col cols="12" sm="6"><v-text-field v-model.number="charity" type="number" min="0" outlined dense hide-details label="Charity" /></v-col>
              <v-col cols="12" sm="6"><v-text-field :value="parentPay" prefix="$" outlined dense readonly hide-details label="Parent responsibility" /></v-col>
            </v-row>
            <p v-if="selectedClass && !validAmounts" class="error--text mb-0">Amounts must be non-negative, and discount plus charity cannot exceed the fee.</p>
            <div class="d-flex justify-end mt-3"><v-btn color="primary" depressed :loading="saving" :disabled="!selectedClass || !validAmounts" @click="submit">Add department</v-btn></div>
          </section>
          <section class="entity-form-dialog__section">
            <div class="entity-form-dialog__section-heading"><span><v-icon size="19" color="primary">mdi-timeline-clock-outline</v-icon></span><div><h3>Department history ({{ history.length }})</h3><p>Recorded assignments, tuition changes, and removals.</p></div></div>
            <div v-if="history.length">
              <div v-for="event in history" :key="event.id" class="student-management-row">
                <div>
                  <strong>{{ event.departmentName }}</strong>
                  <small>{{ historyLabel(event.eventType) }} · {{ formatDate(event.effectiveAt) }}</small>
                  <small v-if="event.metadata && event.metadata.removedBy">By {{ event.metadata.removedBy.name }}</small>
                </div>
                <span>{{ Number(event.parentPay || 0) | currency }} parent</span>
              </div>
            </div>
            <p v-else class="mb-0">No department history recorded yet.</p>
          </section>
        </v-card-text>
        <v-divider />
        <v-card-actions class="profile-dialog-actions entity-form-dialog__actions"><v-btn text @click="close">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
    <EditFee :editDialog="feeDialog" :editStudent="selectedAssignment" @close="feeDialog = false" @submit="refreshAfterChange" />
  </div>
</template>

<script>
import classesService from "@/modules/Classes/service.js";
import studentService from "@/modules/Student/service.js";

const EditFee = () => import("@/modules/Student/components/EditFee.vue");

export default {
  name: "ManageStudentDepartments",
  components: { EditFee },
  props: { editDialog: { default: false }, Student: { type: Object, default: null } },
  data() { return { dialog: false, Classes: [], assignments: [], history: [], selectedClass: null, selectedAssignment: null, feeDialog: false, fee: 0, discount: 0, charity: 0, loadingClasses: false, loadingAssignments: false, saving: false }; },
  computed: {
    availableClasses() { return this.Classes.filter((item) => !this.assignments.some((row) => row.ClassId === item.id)); },
    parentPay() { return Math.max(Number(this.fee || 0) - Number(this.discount || 0) - Number(this.charity || 0), 0); },
    validAmounts() { const amounts = [this.fee, this.discount, this.charity].map(Number); return amounts.every((value) => Number.isFinite(value) && value >= 0) && amounts[1] + amounts[2] <= amounts[0]; },
  },
  watch: {
    editDialog(open) { this.dialog = open; if (open && this.Student) this.load(); else if (!open) this.resetForm(); },
    selectedClass(value) { this.fee = value ? Number(value.defaultFee || 0) : 0; this.discount = 0; this.charity = 0; },
  },
  methods: {
    async load() {
      this.loadingAssignments = true;
      this.loadingClasses = true;
      try {
        const [classes, assignments, history] = await Promise.all([
          classesService.getAll(), studentService.getAllClassStudent({ StudentId: this.Student.id }), studentService.getDepartmentHistory(this.Student.id),
        ]);
        this.Classes = classes.data || [];
        this.assignments = assignments.data || [];
        this.history = history.data || [];
      } catch (error) { this.showError(error, "Could not load department assignments."); }
      finally { this.loadingAssignments = false; this.loadingClasses = false; }
    },
    async refreshAfterChange() { this.feeDialog = false; await this.load(); this.$emit("submit"); },
    editFee(item) { this.selectedAssignment = item; this.feeDialog = true; },
    async remove(item) {
      const result = await this.$swal({ title: "Remove department?", text: `Remove ${this.Student.fullName} from ${item.Class ? item.Class.name : "this department"}?`, icon: "question", showCancelButton: true, confirmButtonColor: "#B71C1C", confirmButtonText: "Remove" });
      if (!result.value) return;
      try { await studentService.removeStudentFromClass(item.id); await this.refreshAfterChange(); }
      catch (error) { this.showError(error, "Could not remove department."); }
    },
    async submit() {
      if (!this.selectedClass || !this.validAmounts) return;
      this.saving = true;
      try {
        await classesService.assignStudent({ ClassId: this.selectedClass.id, StudentId: this.Student.id, fee: Number(this.fee), discount: Number(this.discount), charity: Number(this.charity), sponsor: Number(this.charity), parentPay: this.parentPay });
        this.resetForm(); await this.refreshAfterChange();
      } catch (error) { this.showError(error, "Could not add department."); }
      finally { this.saving = false; }
    },
    historyLabel(type) { return { ASSIGNED: "Assigned", FEE_CHANGED: "Tuition changed", REMOVED: "Removed" }[type] || "Updated"; },
    formatDate(value) { return value ? this.$moment(value).format("MMM D, YYYY") : "Date unavailable"; },
    showError(error, fallback) { this.$swal("Department management", error && error.data && error.data.message ? error.data.message : fallback, "error"); },
    resetForm() { this.selectedClass = null; this.selectedAssignment = null; this.fee = 0; this.discount = 0; this.charity = 0; },
    close() { this.dialog = false; this.resetForm(); this.$emit("close"); },
  },
};
</script>

<style scoped>
.student-management-row { display: flex; align-items: center; gap: 10px; padding: 10px 0; border-bottom: 1px solid #e6ece7; }
.student-management-row:last-child { border-bottom: 0; }
.student-management-row > div { min-width: 0; flex: 1; }
.student-management-row strong, .student-management-row small { display: block; }
.student-management-row small { color: #78857b; }
</style>
